import { useState } from 'react'

import { faArrowRight } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Button, Link, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'

import api from 'common/api'
import useScid from 'common/use-scid'
import { useHistory } from 'components/ReactRouterProvider/context'
import PreviewApplicationButton from 'routes/application/Activate/components/PreviewApplicationButton'

import useCanManageApplications from '../../../useCanManageApplications'

const useStyles = makeStyles((theme) => ({
  container: {
    maxWidth: theme.spacing(68),
  },
  noButton: {
    marginLeft: theme.spacing(2),
  },
}))

type ConfirmConfigurationStepProps = {
  appConfig: {
    verification: {
      submissionVerified: boolean
    }
    firstYearConfiguration: {
      enabled: boolean
    }
    transferConfiguration: {
      enabled: boolean
    }
    id: string
  }
  completeStep: () => void
  readOnlyView?: boolean
}

export const ConfirmConfigurationStep = ({ appConfig, completeStep, readOnlyView }: ConfirmConfigurationStepProps) => {
  const history = useHistory()
  const submissionVerified = appConfig && appConfig.verification && appConfig.verification.submissionVerified
  const firstYearsEnabled = appConfig && appConfig.firstYearConfiguration && appConfig.firstYearConfiguration.enabled
  const transfersEnabled = appConfig && appConfig.transferConfiguration && appConfig.transferConfiguration.enabled
  const firstOrTransfersEnabled = firstYearsEnabled || transfersEnabled
  const [confirming, setConfirming] = useState(false)
  const [confirmationError, setConfirmationError] = useState(false)
  const styles = useStyles()
  const canCompleteStep = useCanManageApplications()
  const scid = useScid()

  if (!appConfig) {
    return null
  }

  const confirmConfiguration = () => {
    setConfirming(true)
    api
      .put(
        `/api/colleges/${scid}/integrations/application/configuration/${appConfig.id}/verification/confirmConfiguration`,
      )
      .then(async () => {
        setConfirmationError(false)
        completeStep()
      })
      .catch(() => {
        setConfirming(false)
        setConfirmationError(true)
      })
  }

  return (
    <Box
      px={4}
      py={1}
    >
      <Typography>{'Have you finished setting up your application in Scoir?'}</Typography>
      <Box
        mt={1}
        mb={3}
      >
        <Typography variant="caption">
          {`By clicking "Yes" you are verifying your application selections for this cycle. After August 1st, some aspects of your configuration can't be updated once your application is activated.`}
        </Typography>
      </Box>
      <Button
        color="primary"
        variant="contained"
        disabled={!submissionVerified || !canCompleteStep || confirming || !firstOrTransfersEnabled}
        onClick={confirmConfiguration}
      >
        Yes
      </Button>
      <Button
        color="primary"
        variant="outlined"
        className={styles.noButton}
        disabled={!canCompleteStep || confirming}
        onClick={() => history.push(`${import.meta.env.BASE_URL}/application/settings`)}
      >
        No, Go to Application Configuration&nbsp;
        <FontAwesomeIcon icon={faArrowRight} />
      </Button>
      {!readOnlyView && <PreviewApplicationButton configurationId={appConfig?.id} />}
      {!firstOrTransfersEnabled && (
        <Box mt={2}>
          <Typography
            variant="caption"
            color="error"
          >
            To activate, you must enable at least one applicant type. Please return to your Application Configuration
            and indicate your intent to accept first-year, transfer, or both types of application.
          </Typography>
        </Box>
      )}
      {confirmationError && (
        <Box mt={2}>
          <Typography
            variant="caption"
            color="error"
          >
            An error occurred while confirming your configuration.
            <Link
              href="https://www.scoir.com/support"
              target="_blank"
              rel="noreferrer"
            >
              Please contact customer support.
            </Link>
          </Typography>
        </Box>
      )}
    </Box>
  )
}

export default ConfirmConfigurationStep
